import { HeadingPages } from '../../../components/HeadingPages'
import { Layout } from '../../../components/Layout'
import { Loader } from '../../../components/ComponentLoader/Loader'
import { ErrorBoundary } from 'react-error-boundary'
import InputFloating from '../../../components/InputFloating'
import { QueryErrorResetBoundary, useQuery } from 'react-query'
import { baseURL } from '../../../services/api'
import { Box, Button, Flex, Table, Tbody, Td, Text, Th, Thead, Tr, useToast } from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { MaisInformacoes } from './MaisInformacoes'
import * as T from '../../../components/Table'
import { DadosDoCliente } from './DadosDoCliente'

async function fetcher({ numero_pedido }) {
  const response = await baseURL.get(`/controle-fedex/listar-info-pedido`, {
    params: {
      numero_pedido,
    },
  })

  const { dados_pedido, info_caixas, info_cliente } = response.data

  return { dados_pedido, info_caixas, info_cliente }
}

const schema = z.object({
  numero_pedido: z.string().min(1, { message: 'Número do pedido é obrigatório' }),
})

export function AuditoriaFrete() {
  const { handleSubmit, register, watch } = useForm({
    resolver: zodResolver(schema),
  })

  const numero_pedido = watch('numero_pedido')

  const toast = useToast({
    position: 'top-right',
  })

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryFn: () => fetcher({ numero_pedido: numero_pedido }),
    onSuccess: (data) => {
      console.log('Sucesso')
      toast({
        title: 'Sucesso',
        description: 'Pedido encontrado com sucesso',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
    },
    onError: () => {
      console.log('Erro')
      toast({
        title: 'Erro',
        description: 'Erro ao encontrar pedido',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    },

    enabled: false,
  })

  async function handleSearch(data) {
    refetch()
  }

  return (
    <>
      <HeadingPages name="Auditoria de frete" />

      <Layout>
        <Flex gap={2} as="form" onSubmit={handleSubmit(handleSearch)} maxW="550px" w="100%" mx="auto">
          <InputFloating label="Número do pedido" {...register('numero_pedido')} />
          <Button colorScheme="blue" type="submit" isLoading={isRefetching || isLoading}>
            Buscar
          </Button>
        </Flex>

        {isLoading && <Loader />}

        {data && (
          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary
                onReset={reset}
                fallbackRender={({ resetErrorBoundary }) => (
                  <Flex>
                    <Button onClick={() => resetErrorBoundary()}>Tentar novamente</Button>
                  </Flex>
                )}
              >
                <Box>
                  <MaisInformacoes infoCaixas={data.info_caixas} />
                </Box>
                <T.Table mt="4">
                  <T.Thead>
                    <T.Tr>
                      <T.Th>Razão Social</T.Th>
                      <T.Th>CNPJ</T.Th>
                      <T.Th>CEP</T.Th>
                      <T.Th>Cidade</T.Th>
                      <T.Th>Município</T.Th>
                      <T.Th>Número Residencial</T.Th>
                    </T.Tr>
                  </T.Thead>
                  <T.Tbody>
                    <T.Tr>
                      <T.Td>{data?.info_cliente.razao_social}</T.Td>
                      <T.Td>{data?.info_cliente.cnpj}</T.Td>
                      <T.Td>{data?.info_cliente.cep}</T.Td>
                      <T.Td>{data?.info_cliente.cidade}</T.Td>
                      <T.Td>{data?.info_cliente.municipio}</T.Td>
                      <T.Td>{data?.info_cliente.numero_residencial}</T.Td>
                    </T.Tr>
                  </T.Tbody>
                </T.Table>
                <T.Table variant="simple" mt="4">
                  <T.Thead>
                    <T.Tr bg="gray.100">
                      <T.Th>Data de cotação</T.Th>
                      <T.Th>Valor do Gris</T.Th>
                      <T.Th>Valor do TDE</T.Th>
                      <T.Th>Agendamento de Entrega</T.Th>
                      <T.Th>Valor do Frete (TNT)</T.Th>
                      <T.Th>Valor do Frete (TNT) + 30%</T.Th>
                      <T.Th>Peso Enviado</T.Th>
                      <T.Th>Volume de caixas</T.Th>
                      <T.Th>Valor total da Nota</T.Th>
                      <T.Th>Baixar XML - FEDEX</T.Th>
                      <T.Th>Baixar XML - INNOVATION</T.Th>
                    </T.Tr>
                  </T.Thead>
                  <T.Tbody>
                    {data?.dados_pedido?.map((item, index) => (
                      <T.Tr key={index}>
                        <T.Td>{item.data_cotacao}</T.Td>
                        <T.Td>R$ {item.gris}</T.Td>
                        <T.Td>R$ {item.tde}</T.Td>
                        <T.Td>R$ {item.agendamento_entrega}</T.Td>
                        <T.Td>R$ {item.valor_total_frete}</T.Td>
                        <T.Td>R$ {item.valor_frete_porcentagem}</T.Td>
                        <T.Td>{item.peso_enviado}</T.Td>
                        <T.Td>{item.volume_caixas}</T.Td>
                        <T.Td>R$ {item.valor_nota}</T.Td>

                        <T.Td>
                          <Text
                            color="blue.500"
                            textDecoration="underline"
                            _hover={{ cursor: 'pointer', filter: 'brightness(.9)' }}
                            as="a"
                            href={item.xml_fedex}
                            download
                          >
                            Baixar XML Fedex
                          </Text>
                        </T.Td>
                        <T.Td>
                          <Text
                            color="blue.500"
                            textDecoration="underline"
                            _hover={{ cursor: 'pointer', filter: 'brightness(.9)' }}
                            as="a"
                            href={item.xml_innovation}
                            download
                          >
                            Baixar XML Innovation
                          </Text>
                        </T.Td>
                      </T.Tr>
                    ))}
                  </T.Tbody>
                </T.Table>
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        )}
      </Layout>
    </>
  )
}
